import { ArrowLeft, Loader2, Play, Upload, X } from "lucide-react";
import { Doc, Id } from "@cvx/_generated/dataModel";
import { convexQuery, useConvexMutation } from "@convex-dev/react-query";
import { useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { APPROVED_STRIPE_CONNECT_COUNTRIES } from "@/constants/approvedCountries";
import ApplicationVideoLink from "./ApplicationVideoLink";
import { Button } from "@/components/ui/button";
import CampaignFAQ from "@/components/Generic/CampaignFAQ";
import CampaignVideos from "@/components/Apps/CampaignVideos";
import { Card } from "@/components/ui/card";
import { ConvexError } from "convex/values";
import { ERRORS } from "~/errors";
import { Info } from "lucide-react";
import { PARTNERSHIP_EVENTS } from "@/constants/analyticsEvents";
import PlatformIcon from "@/components/Generic/PlatformIcon";
import { Slider } from "@/components/ui/slider";
import { Textarea } from "@/components/ui/textarea";
import VideoLink from "@/components/Generic/VideoLink";
import { Route as WelcomeRoute } from "~/src/routes/_app/_auth/dashboard/_layout.welcome";
import { api } from "@cvx/_generated/api";
import { cn } from "@/utils/misc";
import { countries } from "countries-list";
import getUnicodeFlagIcon from "country-flag-icons/unicode";
import { useAccount } from "@/state/useAccount";
import { useForm } from "@tanstack/react-form";
import { useMixpanel } from "@/hooks/useMixpanel";
import { useNavigate } from "@tanstack/react-router";
import { useToast } from "@/components/ui/use-toast";
import { useUploadFiles } from "@xixixao/uploadstuff/react";
import { z } from "zod";

// Component to handle video display for both storage IDs and URLs
function VideoPlayer({ video }: { video: string }) {
  // Check if it's a storage ID or URL
  const isStorageId = !video.startsWith("http");

  const { data: videoUrl } = useQuery({
    ...convexQuery(api.applications.getApplicationVideoUrl, {
      storageId: video as Id<"_storage">,
    }),
    enabled: isStorageId,
  });

  const displayUrl = isStorageId ? videoUrl : video;

  // Check if it's a YouTube URL for embedded player
  const isYouTube = video.includes("youtube.com") || video.includes("youtu.be");
  const isVimeo = video.includes("vimeo.com");
  const isLoom = video.includes("loom.com");

  if (isYouTube) {
    // Extract YouTube video ID and create embed URL
    let videoId = "";
    if (video.includes("youtu.be/")) {
      videoId = video.split("youtu.be/")[1].split("?")[0];
    } else if (video.includes("youtube.com/watch?v=")) {
      videoId = video.split("v=")[1].split("&")[0];
    } else if (video.includes("youtube.com/embed/")) {
      videoId = video.split("/embed/")[1].split("?")[0];
    }

    if (videoId) {
      return (
        <div className="aspect-video w-full">
          <iframe
            src={`https://www.youtube.com/embed/${videoId}`}
            frameBorder="0"
            allowFullScreen
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            className="h-full w-full rounded-lg"
            title="Campaign marketing video"
          />
        </div>
      );
    }
  }

  if (isVimeo) {
    // Extract Vimeo video ID and create embed URL
    const vimeoMatch = video.match(/vimeo\.com\/(\d+)/);
    if (vimeoMatch) {
      const videoId = vimeoMatch[1];
      return (
        <div className="aspect-video w-full">
          <iframe
            src={`https://player.vimeo.com/video/${videoId}`}
            frameBorder="0"
            allowFullScreen
            allow="autoplay; fullscreen; picture-in-picture"
            className="h-full w-full rounded-lg"
            title="Campaign marketing video"
          />
        </div>
      );
    }
  }

  if (isLoom) {
    // Handle Loom URLs - if it's already an embed URL, use it directly
    if (video.includes("/embed/")) {
      return (
        <div className="aspect-video w-full">
          <iframe
            src={video}
            frameBorder="0"
            allowFullScreen
            className="h-full w-full rounded-lg"
            title="Campaign marketing video"
          />
        </div>
      );
    }
    // Convert regular Loom URLs to embed format if needed
    const loomMatch = video.match(/loom\.com\/share\/([a-zA-Z0-9]+)/);
    if (loomMatch) {
      const videoId = loomMatch[1];
      return (
        <div className="aspect-video w-full">
          <iframe
            src={`https://www.loom.com/embed/${videoId}`}
            frameBorder="0"
            allowFullScreen
            className="h-full w-full rounded-lg"
            title="Campaign marketing video"
          />
        </div>
      );
    }
  }

  // For uploaded video files (storage IDs) or direct video URLs
  if (displayUrl) {
    return (
      <div className="aspect-video w-full">
        <video
          src={displayUrl}
          controls
          preload="metadata"
          className="h-full w-full rounded-lg object-contain bg-black"
        >
          Your browser does not support the video tag.
        </video>
      </div>
    );
  }

  // Loading or error state
  return (
    <div className="aspect-video w-full bg-muted rounded-lg flex items-center justify-center">
      <div className="text-center">
        <Play className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
        <p className="text-sm text-muted-foreground">Loading video...</p>
      </div>
    </div>
  );
}

// Validation schema for the application form
const applicationSchema = z.object({
  additionalInfo: z.string().optional(),
});

type ApplicationData = z.infer<typeof applicationSchema>;

interface UploadedFile {
  id: Id<"_storage">;
  name: string;
  type: "productDemo";
}

interface UploadResponse {
  storageId: Id<"_storage">;
}

export default function PartnershipApplication({
  selectedCampaign,
  clearPendingCampaignId,
  onSelectCampaign,
}: {
  selectedCampaign: (Doc<"campaigns"> & { app: Doc<"apps"> }) | null;
  clearPendingCampaignId: () => void;
  onSelectCampaign?: (campaignId: Id<"campaigns">) => void;
}) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const navigate = useNavigate();
  const { setContractId } = useAccount();
  const { track } = useMixpanel();

  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [viewsSlider, setViewsSlider] = useState([500000]); // Default to 500k views
  const [termsAccepted, setTermsAccepted] = useState(false);
  const productDemoInputRef = useRef<HTMLInputElement>(null);

  const generateUploadUrl = useConvexMutation(api.app.generateUploadUrl);

  // Get user data to check placeOfResidence
  const { data: user } = useQuery(convexQuery(api.app.getCurrentUser, {}));

  // Fetch available campaigns for the sidebar
  const { data: availableCampaignsData } = useQuery(
    convexQuery(
      api.partnerships.getPaginatedCampaignsAvailableForPartnerships,
      {
        paginationOpts: { numItems: 10, cursor: null },
      },
    ),
  );

  // Fetch user's active partnerships to filter them out
  const { data: userPartnershipsData } = useQuery(
    convexQuery(api.partnerships.getPaginatedCreatorPartnershipsForCreator, {
      type: "contracts",
      paginationOpts: { numItems: 100, cursor: null },
    }),
  );

  // Filter out campaigns the user has already enrolled in
  const enrolledCampaignIds = new Set(
    userPartnershipsData?.page?.map((p) => p.campaignId) || [],
  );

  const availableCampaigns = (availableCampaignsData?.page || [])
    .filter((campaign) => !enrolledCampaignIds.has(campaign._id))
    .sort((a, b) => {
      // Sort by CPM (descending) - campaigns with higher CPM first
      const cpmA = a.predefinedCpm || 0;
      const cpmB = b.predefinedCpm || 0;
      return cpmB - cpmA;
    });

  // Create eligible countries list with proper typing - use campaign's specific eligible countries
  const eligibleCountries = Object.entries(
    countries as unknown as Record<string, { name: string }>,
  )
    .filter(
      ([code]) =>
        selectedCampaign?.eligibleCountries
          ? selectedCampaign.eligibleCountries.includes(code)
          : APPROVED_STRIPE_CONNECT_COUNTRIES.includes(code), // fallback for campaigns without specific countries
    )
    .map(([code, country]) => ({
      code,
      name: country.name,
      flag: getUnicodeFlagIcon(code),
    }))
    .sort((a, b) => a.name.localeCompare(b.name));

  // Check if user's country is eligible
  const isUserCountryEligible = useMemo(() => {
    if (!user?.placeOfResidence || !selectedCampaign) return true; // Allow if no country set yet

    const eligibleCountryCodes = selectedCampaign.eligibleCountries?.length
      ? selectedCampaign.eligibleCountries
      : APPROVED_STRIPE_CONNECT_COUNTRIES;

    return eligibleCountryCodes.includes(user.placeOfResidence);
  }, [user?.placeOfResidence, selectedCampaign]);

  // Get user's country name for error message
  const userCountryName = useMemo(() => {
    if (!user?.placeOfResidence) return null;
    const countryData = (countries as Record<string, { name: string }>)[
      user.placeOfResidence
    ];
    return countryData?.name || user.placeOfResidence;
  }, [user?.placeOfResidence]);

  const { data: applicationData } = useQuery(
    convexQuery(api.applications.getApplicationAndContractForCampaign, {
      campaignId: selectedCampaign?._id,
    }),
  );

  const applications = applicationData?.applications || [];
  const existingContract = applicationData?.contract;
  const canReapply = applicationData?.canReapply ?? true;

  const hasPendingApplication = applications.some(
    (app) => app.status === "pending",
  );

  const { mutateAsync: submitApplication, isPending: isSubmitting } =
    useMutation({
      mutationFn: useConvexMutation(api.applications.submitApplication),
    });

  const { mutateAsync: joinCampaign, isPending: isJoining } = useMutation({
    mutationFn: useConvexMutation(
      api.partnerships.joinCampaignWithoutApplication,
    ),
  });

  const { startUpload } = useUploadFiles(generateUploadUrl, {
    onUploadComplete: async (uploaded) => {
      const uploadResponse = uploaded[0].response as UploadResponse;

      setUploadedFiles((prev) => [
        ...prev,
        {
          id: uploadResponse.storageId,
          name: uploaded[0].name,
          type: "productDemo",
        },
      ]);

      setIsUploading(false);
      toast({
        title: "File uploaded successfully",
        description: `${uploaded[0].name} has been uploaded`,
      });
    },
    onUploadError: () => {
      setIsUploading(false);
      toast({
        title: "Upload failed",
        description: "There was an error uploading your file",
        variant: "destructive",
      });
    },
  });

  const handleFileUpload = (files: File[]) => {
    if (files.length === 0) return;

    setIsUploading(true);
    startUpload(files);
  };

  const removeFile = (fileId: Id<"_storage">) => {
    setUploadedFiles((prev) => prev.filter((file) => file.id !== fileId));
  };

  const form = useForm<ApplicationData>({
    defaultValues: {
      additionalInfo: undefined,
    },
    onSubmit: async ({ value }) => {
      if (!selectedCampaign) return;

      if (uploadedFiles.length === 0) {
        toast({
          title: "Missing files",
          description: "Please upload at least one product demo video",
          variant: "destructive",
        });
        return;
      }

      try {
        const validatedData = applicationSchema.parse(value);

        await submitApplication({
          campaign: selectedCampaign._id,
          links: uploadedFiles.map((file) => file.id),
          additionalInfo: validatedData.additionalInfo,
        });

        // Track campaign enrollment
        track(PARTNERSHIP_EVENTS.APPLICATION_SUBMITTED, {
          user_id: user?._id,
          campaign_id: selectedCampaign._id,
          campaign_name: selectedCampaign.name,
          app_id: selectedCampaign.appId,
          app_name: selectedCampaign.app.name,
          has_additional_info: !!validatedData.additionalInfo,
          videos_uploaded: uploadedFiles.length,
          timestamp: new Date().toISOString(),
        });

        toast({
          title: "Application submitted successfully",
          description: "You will be notified when your application is reviewed",
        });

        // Reset form
        setUploadedFiles([]);
      } catch (error) {
        console.error("Submission error:", error);
        toast({
          title: "Failed to submit application",
          description:
            error instanceof ConvexError
              ? error.data
              : ERRORS.SOMETHING_WENT_WRONG,
          variant: "destructive",
        });
      }
    },
  });

  const handleJoinCampaign = async () => {
    if (!selectedCampaign) return;

    try {
      const contractId = await joinCampaign({
        campaignId: selectedCampaign._id,
      });

      // Track campaign enrollment (no application required)
      track(PARTNERSHIP_EVENTS.CAMPAIGN_JOINED, {
        user_id: user?._id,
        campaign_id: selectedCampaign._id,
        campaign_name: selectedCampaign.name,
        app_id: selectedCampaign.appId,
        app_name: selectedCampaign.app.name,
        application_required: false,
        contract_id: contractId,
        timestamp: new Date().toISOString(),
      });

      // Invalidate the query to refresh the data and update the UI
      await queryClient.invalidateQueries({
        queryKey: convexQuery(
          api.applications.getApplicationAndContractForCampaign,
          {
            campaignId: selectedCampaign._id,
          },
        ).queryKey,
      });

      toast({
        title: "Successfully joined campaign!",
        description: "You are now part of this campaign. Welcome aboard!",
      });

      // Set contract ID and redirect to welcome screen
      setContractId(contractId);
      await navigate({
        to: WelcomeRoute.fullPath,
        search: { appName: selectedCampaign.app.name },
        replace: true,
      });
    } catch (error) {
      console.error("Join campaign error:", error);
      toast({
        title: "Failed to join campaign",
        description:
          error instanceof ConvexError
            ? error.data
            : ERRORS.SOMETHING_WENT_WRONG,
        variant: "destructive",
      });
    }
  };

  if (!selectedCampaign) {
    return null;
  }

  return (
    <div className="flex gap-6 h-full">
      {/* Sidebar - Hidden on mobile, visible on lg+ screens */}
      <div className="hidden lg:block w-80 flex-shrink-0 space-y-4 sticky top-32 self-start max-h-screen overflow-y-auto">
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={() => {
            // Clear both URL params and pending campaign ID
            clearPendingCampaignId();
            navigate({
              search: {},
              replace: true,
            });
          }}
          className="w-full justify-start"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>

        {/* Opportunities List */}
        <Card className="p-4">
          <h3 className="text-lg font-semibold text-primary mb-4">
            Opportunities
          </h3>
          <div className="space-y-2">
            {availableCampaigns.map((campaign) => (
              <button
                key={campaign._id}
                onClick={() => {
                  if (onSelectCampaign) {
                    onSelectCampaign(campaign._id);
                  }
                }}
                className={cn(
                  "w-full text-left p-3 rounded-lg transition-colors",
                  "hover:bg-muted/50",
                  campaign._id === selectedCampaign._id
                    ? "bg-primary/10 border border-primary/20"
                    : "bg-background",
                )}
              >
                <div className="flex items-start gap-3">
                  <img
                    src={campaign.app.logo}
                    alt={campaign.app.name}
                    className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium text-sm text-primary truncate">
                      {campaign.app.name}
                    </h4>
                    {campaign.predefinedCpm && (
                      <p className="text-xs text-primary/60 mt-1">
                        ${(campaign.predefinedCpm * 1000).toLocaleString()} per
                        million views
                      </p>
                    )}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </Card>
      </div>

      {/* Main Content */}
      <div className="flex-1 space-y-6 overflow-y-auto w-full">
        {/* Mobile Back Button - Only visible on mobile */}
        <Button
          variant="ghost"
          onClick={() => {
            // Clear both URL params and pending campaign ID
            clearPendingCampaignId();
            navigate({
              search: {},
              replace: true,
            });
          }}
          className="lg:hidden mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Opportunities
        </Button>

        <Card className="p-6 space-y-6">
          {/* Title Section */}
          <div className="text-center border-b pb-6">
            <h2 className="text-2xl font-bold text-primary">
              {selectedCampaign.type === "challenge"
                ? "Join the Challenge"
                : "Partnership Application"}
            </h2>
            <p className="text-base text-primary/60 mt-1">
              {selectedCampaign.type === "challenge"
                ? "Earn rewards by creating content that meets the requirements."
                : "Apply to partner with " + selectedCampaign.app.name}
            </p>
          </div>

          {/* App Info Section */}
          <div className="flex items-center gap-3">
            <img
              className="h-8 w-8 rounded-full object-cover"
              alt={selectedCampaign.app.name}
              src={selectedCampaign.app.logo}
            />
            <div>
              <h3 className="text-xl font-semibold text-primary">
                {selectedCampaign.name}
              </h3>
              <p className="text-sm text-primary/60">
                {selectedCampaign.app.description}
              </p>
            </div>
          </div>

          <div className="space-y-6">
            <div>
              <h4 className="font-semibold text-primary text-base">
                {selectedCampaign.type === "challenge"
                  ? `About ${selectedCampaign.app.name}`
                  : "What We're Looking For"}
              </h4>
              {/* Show first marketing video if available, or hardcoded video for specific campaign */}
              {selectedCampaign.videos && selectedCampaign.videos.length > 0 ? (
                <div className="mt-4">
                  <VideoPlayer video={selectedCampaign.videos[0]} />
                </div>
              ) : selectedCampaign._id ===
                "nd7f46a8fdvef2b71s7m4tpgkn7kr5cg" ? (
                <div className="mt-4">
                  <VideoPlayer video="https://www.loom.com/embed/73a649cbc32a460cadfcaf18027941da?sid=311d2c82-34d4-47bc-ba49-9df8fa6bf86b" />
                </div>
              ) : null}
              {selectedCampaign.creatorBrief ? (
                <div
                  className="mt-2 text-sm text-primary/60 rich-text-display"
                  dangerouslySetInnerHTML={{
                    __html: selectedCampaign.creatorBrief,
                  }}
                />
              ) : (
                <p className="mt-2 text-sm text-primary/60">
                  No brief provided
                </p>
              )}
            </div>

            {/* How Challenges Work - Only for challenge campaigns */}
            {selectedCampaign.type === "challenge" && (
              <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                <h4 className="font-semibold text-primary text-base mb-3">
                  How Challenges Work
                </h4>
                <div className="space-y-2">
                  <div className="flex items-start gap-2">
                    <span className="text-blue-600 dark:text-blue-400 font-semibold">
                      1.
                    </span>
                    <p className="text-sm text-blue-800 dark:text-blue-300">
                      You submit a piece of content
                    </p>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="text-blue-600 dark:text-blue-400 font-semibold">
                      2.
                    </span>
                    <p className="text-sm text-blue-800 dark:text-blue-300">
                      It gets reviewed and approved by the brand
                    </p>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="text-blue-600 dark:text-blue-400 font-semibold">
                      3.
                    </span>
                    <p className="text-sm text-blue-800 dark:text-blue-300">
                      You post
                    </p>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="text-blue-600 dark:text-blue-400 font-semibold">
                      4.
                    </span>
                    <p className="text-sm text-blue-800 dark:text-blue-300">
                      You start earning
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Campaign Marketing Videos - show remaining videos if more than 1 */}
            {selectedCampaign.videos && selectedCampaign.videos.length > 1 && (
              <CampaignVideos
                videos={selectedCampaign.videos.slice(1)}
                title="Additional Walkthroughs"
                className="my-6"
              />
            )}

            {/* Allowed Channels */}
            {selectedCampaign.allowedChannels &&
              selectedCampaign.allowedChannels.length > 0 && (
                <div>
                  <h4 className="font-semibold text-primary text-base">
                    Allowed Platforms
                  </h4>
                  <div className="mt-2 flex gap-3">
                    {selectedCampaign.allowedChannels.map((channel) => (
                      <div
                        key={channel}
                        className="flex items-center gap-2 px-3 py-2 bg-muted rounded-lg"
                      >
                        <PlatformIcon platform={channel} />
                        <span className="text-sm font-medium capitalize">
                          {channel}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

            {/* Predefined CPM */}
            {selectedCampaign.predefinedCpm && (
              <div>
                <h4 className="font-semibold text-primary text-base">
                  Payment Rate
                </h4>
                <div className="mt-2 flex items-center gap-2 px-3 py-2 bg-green-50 dark:bg-green-900/20 rounded-lg">
                  <span className="text-sm text-green-800 dark:text-green-300">
                    ${selectedCampaign.predefinedCpm} CPM (per 1,000 views)
                  </span>
                </div>

                {/* Earnings Calculator - Only for Challenge Campaigns */}
                {selectedCampaign.type === "challenge" && (
                  <div className="mt-3 p-4 bg-gray-50 dark:bg-gray-900/20 rounded-lg">
                    <h5 className="font-semibold text-sm text-primary mb-3">
                      Earnings Calculator
                    </h5>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-primary/60">Views</span>
                        <span className="text-sm font-medium text-primary">
                          {viewsSlider[0].toLocaleString()}
                        </span>
                      </div>
                      <Slider
                        value={viewsSlider}
                        onValueChange={setViewsSlider}
                        max={1000000}
                        min={0}
                        step={10000}
                        className="w-full"
                      />
                      <div className="flex items-center justify-between pt-2">
                        <span className="text-sm text-primary/60">
                          Potential Earnings
                        </span>
                        <span className="text-lg font-semibold text-green-600 dark:text-green-400">
                          $
                          {(
                            (viewsSlider[0] / 1000) *
                            selectedCampaign.predefinedCpm
                          ).toLocaleString(undefined, {
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 0,
                          })}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Validation Requirements */}
            {selectedCampaign.validationRequirements && (
              <div>
                <h4 className="font-semibold text-primary text-base">
                  Content Requirements
                </h4>
                <div className="mt-2 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                  <div
                    className="text-sm text-blue-800 dark:text-blue-300 rich-text-display"
                    dangerouslySetInnerHTML={{
                      __html: selectedCampaign.validationRequirements,
                    }}
                  />
                </div>
              </div>
            )}

            {/* Campaign Budget */}
            {selectedCampaign.prizePool && (
              <div>
                <h4 className="font-semibold text-primary text-base">
                  Campaign Budget
                </h4>
                <div className="mt-2 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-amber-800 dark:text-amber-300">
                      Total Budget Cap
                    </span>
                    <span className="text-lg font-semibold text-amber-900 dark:text-amber-200">
                      ${selectedCampaign.prizePool.toLocaleString()}
                    </span>
                  </div>
                  <p className="text-xs text-amber-700 dark:text-amber-400 mt-2">
                    Once approved video earnings reach this amount, new posts
                    will be disabled and views will be frozen.
                  </p>
                </div>
              </div>
            )}

            {(selectedCampaign.budgetCpmMin ||
              selectedCampaign.budgetCpmMax ||
              selectedCampaign.budgetFlatFeeMin ||
              selectedCampaign.budgetFlatFeeMax) && (
              <div>
                <h4 className="font-semibold text-primary text-base">
                  Budget Ranges
                </h4>
                <div className="mt-2 space-y-2">
                  {(selectedCampaign.budgetCpmMin ||
                    selectedCampaign.budgetCpmMax) && (
                    <p className="text-sm text-primary/60">
                      CPM: ${selectedCampaign.budgetCpmMin ?? 0} - $
                      {selectedCampaign.budgetCpmMax}
                    </p>
                  )}
                  {(selectedCampaign.budgetFlatFeeMin ||
                    selectedCampaign.budgetFlatFeeMax) && (
                    <p className="text-sm text-primary/60">
                      Flat Fee: ${selectedCampaign.budgetFlatFeeMin ?? 0} - $
                      {selectedCampaign.budgetFlatFeeMax}
                    </p>
                  )}
                </div>
              </div>
            )}

            {selectedCampaign.urls && selectedCampaign.urls.length > 0 && (
              <div>
                <h4 className="font-semibold text-primary text-base">
                  Useful Links
                </h4>
                <div className="mt-2 space-y-4">
                  {selectedCampaign.urls.map((urlItem, index) => (
                    <VideoLink
                      key={index}
                      url={urlItem.url}
                      title={urlItem.title}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Eligible Countries Section */}
            <div>
              <h4 className="font-semibold text-primary text-base">
                Eligible Countries
              </h4>
              <p className="text-sm text-primary/60 mb-3">
                This campaign is available to creators located in the following
                countries:
              </p>

              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {eligibleCountries.map((country) => (
                  <div
                    key={country.code}
                    className="flex items-center gap-2 px-3 py-2 bg-muted rounded-lg"
                  >
                    <span className="text-lg">{country.flag}</span>
                    <span className="text-sm font-medium">{country.name}</span>
                  </div>
                ))}
              </div>

              <div className="flex items-start space-x-2 p-3 bg-blue-50 dark:bg-blue-950/20 rounded-lg mt-3">
                <Info className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" />
                <div className="text-xs text-blue-700 dark:text-blue-300">
                  <p className="font-medium mb-1">Payment Requirements:</p>
                  <p>
                    To receive payments, you must be located in one of these
                    countries and have valid banking information. This ensures
                    we can process payments securely and comply with
                    international regulations.
                  </p>
                </div>
              </div>
            </div>

            {/* Key Terms & Conditions */}
            <div className="pt-6 border-t">
              <h4 className="font-semibold text-primary text-base mb-3">
                Key Terms & Conditions
              </h4>
              <div className="space-y-3 text-sm text-primary/70">
                {selectedCampaign.predefinedCpm && (
                  <p>
                    • You'll receive ${selectedCampaign.predefinedCpm} for every
                    1,000 views for every video that gets approved by{" "}
                    {selectedCampaign.app.name} team. To qualify for this CPM
                    reward, you must post your video before the campaign ends.
                    From the time you post, any view up to 7 days counts.
                    {selectedCampaign.prizePool && (
                      <>
                        {" "}
                        Please recall that budget on this campaign is limited.
                        We’d recommend posting as soon as your video gets
                        approved to ensure you don’t miss out on available
                        budget.
                      </>
                    )}
                  </p>
                )}
                <p>
                  • Payment will be delivered within 14 days of the view count
                  window having ended. We can only provide payments via Stripe
                  Connect, so we'll ask you to enroll into Stripe Connect before
                  you submit your first piece of content.
                </p>
                <p>
                  • Boosting content through ads or bot activity is not allowed;
                  any video that breaches this will be disqualified and receive
                  no remuneration.
                </p>
                <p>
                  • Any approved content will need to be published with a
                  hashtag{" "}
                  <span className="font-mono text-xs text-primary">#ad</span> in
                  order to qualify for the CPM reward.
                </p>
                <p>
                  • Content that you submit can be promoted by{" "}
                  {selectedCampaign.app.name} on its own channels, incl. through
                  ads. By submitting a video, you grant{" "}
                  {selectedCampaign.app.name} the license to do this.
                </p>
                <p className="mt-3">
                  For more information about T&Cs,{" "}
                  <a
                    href="/terms"
                    className="text-primary hover:underline font-medium"
                  >
                    click here
                  </a>
                  .
                </p>
              </div>
            </div>

            {/* Join Campaign Section - Only for campaigns that don't require applications */}
            {selectedCampaign.applicationNotRequired &&
              (!existingContract ||
                (existingContract.status !== "active" &&
                  existingContract.status !== "pendingApproval")) && (
                <div className="mt-6 pt-6 border-t">
                  <div className="text-center">
                    <h4 className="text-lg font-semibold text-primary mb-2">
                      Ready to Join?
                    </h4>
                    <p className="text-sm text-primary/60 mb-4">
                      No application required! You can join this campaign
                      instantly and start creating content.
                    </p>

                    {/* Terms and Conditions Checkbox */}
                    <div className="flex items-start gap-2 p-4 bg-muted/50 rounded-lg mb-4 max-w-md mx-auto">
                      <input
                        type="checkbox"
                        id="terms-checkbox-app"
                        checked={termsAccepted}
                        onChange={(e) => setTermsAccepted(e.target.checked)}
                        className="mt-1 rounded border-gray-300"
                      />
                      <label
                        htmlFor="terms-checkbox-app"
                        className="text-sm text-primary/80 cursor-pointer text-left"
                      >
                        I agree to the{" "}
                        <a
                          href="/terms"
                          target="_blank"
                          className="text-primary hover:underline font-medium"
                        >
                          Terms and Conditions
                        </a>
                      </label>
                    </div>

                    <Button
                      onClick={handleJoinCampaign}
                      disabled={
                        isJoining || !isUserCountryEligible || !termsAccepted
                      }
                      className="w-full max-w-md text-lg py-6"
                      size="lg"
                    >
                      {isJoining ? (
                        <>
                          <Loader2 className="animate-spin mr-2" />
                          Enrolling...
                        </>
                      ) : (
                        "Get Started"
                      )}
                    </Button>

                    {/* Show eligibility error below button */}
                    {!isUserCountryEligible && (
                      <div className="mt-3 p-3 border border-red-200 dark:border-red-800 rounded-lg bg-red-50 dark:bg-red-950/20 max-w-md mx-auto">
                        <div className="flex items-start gap-2">
                          <Info className="h-4 w-4 text-red-500 mt-0.5 flex-shrink-0" />
                          <div>
                            <p className="text-sm font-medium text-red-800 dark:text-red-300">
                              This campaign is not available in{" "}
                              {userCountryName || "your country"}. Please check
                              the eligible countries list above.
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
          </div>
        </Card>

        {/* New section for campaigns that don't require applications */}
        {selectedCampaign.applicationNotRequired ? (
          <div className="space-y-6">
            {/* Check if user already has an active partnership */}
            {existingContract &&
            (existingContract.status === "active" ||
              existingContract.status === "pendingApproval") ? (
              <Card className="space-y-4 p-6">
                <div className="flex items-center justify-between">
                  <h4 className="font-medium text-primary text-base">
                    Partnership Status
                  </h4>
                  <span
                    className={`rounded-full px-3 py-1 text-sm capitalize ${
                      existingContract.status === "active"
                        ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200"
                        : "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-200"
                    }`}
                  >
                    {existingContract.status === "pendingApproval"
                      ? "Pending Approval"
                      : existingContract.status}
                  </span>
                </div>

                <div className="flex items-center gap-2 rounded-lg bg-green-50 dark:bg-green-900/20 p-3">
                  <Info className="h-5 w-5 text-green-600 dark:text-green-400" />
                  <div>
                    <p className="text-sm font-medium text-green-800 dark:text-green-300">
                      {existingContract.status === "active"
                        ? "You're already part of this campaign!"
                        : "Partnership pending approval"}
                    </p>
                    <p className="text-sm text-green-700 dark:text-green-400">
                      {existingContract.status === "active"
                        ? "You can start creating content and earning from this campaign."
                        : "Please wait for the partnership terms to be approved."}
                    </p>
                  </div>
                </div>
              </Card>
            ) : null}
          </div>
        ) : (
          <div className="space-y-6">
            {/* Show application history if there are previous applications */}
            {applications.length > 0 && (
              <Card className="space-y-4 p-6">
                <h4 className="font-medium text-primary text-base">
                  Application History
                </h4>
                <p className="text-sm text-primary/60 mb-4">
                  {hasPendingApplication
                    ? "Your application history for this campaign (you have a pending application):"
                    : "Your previous applications for this campaign:"}
                </p>
                <div className="space-y-4">
                  {applications.map((application, index) => (
                    <div
                      key={application._id}
                      className={`border-l-2 pl-4 ${
                        application.status === "pending"
                          ? "border-yellow-400"
                          : "border-border"
                      }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <h5 className="text-sm font-medium text-primary">
                          Application #{applications.length - index}
                          {application.status === "pending" && (
                            <span className="ml-2 text-xs text-yellow-600 dark:text-yellow-400">
                              (Current)
                            </span>
                          )}
                        </h5>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-primary/60">
                            {new Date(
                              application._creationTime,
                            ).toLocaleDateString()}
                          </span>
                          <span
                            className={`rounded-full px-2 py-1 text-xs capitalize ${
                              application.status === "pending"
                                ? "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-200"
                                : application.status === "approved"
                                  ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200"
                                  : "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-200"
                            }`}
                          >
                            {application.status}
                          </span>
                        </div>
                      </div>

                      {application.status === "rejected" &&
                        application.rejectionReason && (
                          <div className="mb-2 flex items-center gap-2 rounded-lg bg-destructive/10 p-2">
                            <Info className="h-4 w-4 text-destructive" />
                            <p className="text-xs text-destructive">
                              {application.rejectionReason}
                            </p>
                          </div>
                        )}

                      {application.message && (
                        <div className="mb-2">
                          <p className="text-xs font-medium text-primary/80 mb-1">
                            Additional Info:
                          </p>
                          <p className="text-xs text-primary/60">
                            {application.message}
                          </p>
                        </div>
                      )}

                      <div className="space-y-1">
                        <p className="text-xs font-medium text-primary/80">
                          Submitted Videos:
                        </p>
                        {application.links?.map((link, linkIndex) => (
                          <ApplicationVideoLink
                            key={linkIndex}
                            storageId={link as Id<"_storage"> | string}
                            index={linkIndex}
                          />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            )}

            {!canReapply && (
              <Card className="space-y-4 p-6">
                <div className="flex items-center gap-2 rounded-lg bg-blue-50 dark:bg-blue-900/20 p-3">
                  <Info className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                  <p className="text-sm text-blue-800 dark:text-blue-300">
                    {hasPendingApplication
                      ? "Your application is being reviewed. You cannot submit a new application while one is pending."
                      : "You have an active partnership or approved application for this campaign."}
                  </p>
                </div>
              </Card>
            )}

            {canReapply && (
              <div className="space-y-6">
                {existingContract && canReapply && (
                  <Card className="space-y-4 p-6">
                    <div className="flex items-center justify-between">
                      <h4 className="font-medium text-primary text-base">
                        Previous Partnership
                      </h4>
                      <span
                        className={`rounded-full px-3 py-1 text-sm capitalize ${
                          existingContract.status === "completed"
                            ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200"
                            : "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-200"
                        }`}
                      >
                        {existingContract.status}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 rounded-lg bg-blue-50 dark:bg-blue-900/20 p-3">
                      <Info className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                      <p className="text-sm text-blue-800 dark:text-blue-300">
                        Your previous partnership has{" "}
                        {existingContract.status === "completed"
                          ? "completed"
                          : "ended"}
                        . You can submit a new application below.
                      </p>
                    </div>
                  </Card>
                )}

                {/* Show application form only if user can reapply */}
                {canReapply ? (
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      form.handleSubmit();
                    }}
                    className="space-y-6"
                  >
                    <div className="space-y-6">
                      <div className="space-y-6">
                        <div>
                          <h4 className="font-medium text-primary text-base">
                            Product Demo Videos
                          </h4>
                          <p className="text-sm text-primary/60">
                            Upload 1-2 videos demonstrating how you would
                            promote {selectedCampaign.app.name}.
                          </p>
                        </div>

                        <div className="space-y-4">
                          <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-border p-6">
                            {isUploading ? (
                              <>
                                <Loader2 className="mb-2 h-8 w-8 animate-spin text-primary/60" />
                                <p className="mb-1 text-sm font-medium">
                                  Uploading video...
                                </p>
                                <p className="text-xs text-primary/60">
                                  Please wait while we process your video
                                </p>
                              </>
                            ) : (
                              <>
                                <Upload className="mb-2 h-8 w-8 text-primary/60" />
                                <p className="mb-1 text-sm font-medium">
                                  Upload product demo videos
                                </p>
                                <p className="mb-4 text-xs text-primary/60">
                                  Drag and drop or click to select video files
                                </p>
                                <input
                                  ref={productDemoInputRef}
                                  type="file"
                                  accept="video/*"
                                  multiple
                                  className="hidden"
                                  onChange={(e) => {
                                    const files = Array.from(
                                      e.target.files || [],
                                    );
                                    handleFileUpload(files);
                                  }}
                                />
                                <Button
                                  type="button"
                                  variant="outline"
                                  onClick={() =>
                                    productDemoInputRef.current?.click()
                                  }
                                  disabled={isUploading}
                                >
                                  Select Videos
                                </Button>
                              </>
                            )}
                          </div>

                          {/* Display uploaded files */}
                          {uploadedFiles.length > 0 && (
                            <div className="space-y-2">
                              <h5 className="text-sm font-medium">
                                Uploaded Videos:
                              </h5>
                              {uploadedFiles.map((file) => (
                                <div
                                  key={file.id}
                                  className="flex items-center justify-between p-2 bg-muted rounded"
                                >
                                  <span className="text-sm">{file.name}</span>
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => removeFile(file.id)}
                                  >
                                    <X className="h-4 w-4" />
                                  </Button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    <div>
                      <h4 className="font-medium text-primary text-base">
                        Anything Else?
                      </h4>
                      <p className="text-sm text-primary/60">
                        Share any additional information that might be relevant
                        to your application
                      </p>

                      <form.Field
                        name="additionalInfo"
                        children={(field) => (
                          <div className="mt-2 flex flex-col gap-1.5">
                            <Textarea
                              placeholder="Additional information (optional)"
                              value={field.state.value}
                              onBlur={field.handleBlur}
                              onChange={(e) =>
                                field.handleChange(e.target.value)
                              }
                              className="bg-transparent"
                              rows={4}
                            />
                          </div>
                        )}
                      />
                    </div>

                    <Button
                      type="submit"
                      className="w-full"
                      disabled={isSubmitting || !isUserCountryEligible}
                    >
                      {isSubmitting ? (
                        <Loader2 className="animate-spin" />
                      ) : (
                        "Submit Application"
                      )}
                    </Button>

                    {/* Show eligibility error below submit button */}
                    {!isUserCountryEligible && (
                      <div className="mt-3 p-3 border border-red-200 dark:border-red-800 rounded-lg bg-red-50 dark:bg-red-950/20">
                        <div className="flex items-start gap-2">
                          <Info className="h-4 w-4 text-red-500 mt-0.5 flex-shrink-0" />
                          <div>
                            <p className="text-sm font-medium text-red-800 dark:text-red-300">
                              This campaign is not available in{" "}
                              {userCountryName || "your country"}. Please check
                              the eligible countries list above.
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </form>
                ) : (
                  <Card className="space-y-4 p-6">
                    <div className="flex items-center gap-2 rounded-lg bg-yellow-50 dark:bg-yellow-900/20 p-4">
                      <Info className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />
                      <div>
                        <p className="text-sm font-medium text-yellow-800 dark:text-yellow-300">
                          Cannot Submit New Application
                        </p>
                        <p className="text-sm text-yellow-700 dark:text-yellow-400">
                          {hasPendingApplication
                            ? "You have a pending application that needs to be reviewed first."
                            : "You have an active partnership and cannot submit a new application."}
                        </p>
                      </div>
                    </div>
                  </Card>
                )}
              </div>
            )}
          </div>
        )}

        {/* FAQ Section */}
        {selectedCampaign.type === "challenge" && <CampaignFAQ />}
      </div>
    </div>
  );
}
